import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Clapperboard,
  Eye,
  FileStack,
  Film,
  ListChecks,
  Loader2,
  Network,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useLcs } from '@/hooks/useLcs';
import { ATOMIZERS } from '@/lib/atomize';
import {
  persistenceMode,
  type Capabilities,
  type EnrichEventInput,
  type EventEnrichment,
  type GenreOption,
  type ProduceOptions,
} from '@/lib/repo';
import type { AtomType, NewsAtom, Source, StoryEvent } from '@/lib/types';
import { PipelineStepper, type Step } from '@/components/PipelineStepper';
import { AtomCard } from '@/components/AtomCard';
import { AtomCloud } from '@/components/AtomCloud';
import { MediaViewer } from '@/components/MediaViewer';
import { EventAggregator } from '@/components/EventAggregator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export default function TopicWorkspace() {
  const { id = '' } = useParams();
  const {
    topics,
    sources,
    atoms,
    events,
    error,
    loadBundle,
    atomizeTopic,
    updateAtomStatus,
    approveAll,
    publishToIconik,
    produce,
    ingestIntoTopic,
    clusterEvents,
    saveEventsFor,
    enrichEventDraft,
    produceEvent,
    loadCapabilities,
    renameTopic,
    deleteSource,
  } = useLcs();

  const [loaded, setLoaded] = useState(false);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [step, setStep] = useState<string>('sources');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const initialized = useRef(false);

  const topic = topics.find((t) => t.id === id);
  const topicSources = useMemo(() => sources.filter((s) => s.topicId === id), [sources, id]);
  const topicAtoms = useMemo(() => atoms.filter((a) => a.topic_id === id), [atoms, id]);
  const topicEvents = useMemo(() => events.filter((e) => e.topic_id === id), [events, id]);

  useEffect(() => {
    setLoaded(false);
    initialized.current = false;
    void loadBundle(id).finally(() => setLoaded(true));
  }, [id, loadBundle]);

  // Load Story Engine genres/aspects once (cloud only) for the produce picker.
  useEffect(() => {
    if (persistenceMode !== 'cloud') return;
    void loadCapabilities().then((c) => c && setCaps(c));
  }, [loadCapabilities]);

  // Footage/CNN genres + aspects for the produce controls (fallback until loaded).
  const footageGenres: GenreOption[] = (caps?.genres ?? []).filter((g) => g.footage);
  const genreOptions: GenreOption[] = footageGenres.length
    ? footageGenres
    : [
        { genre_key: 'cnn_social', display_name: 'CNN Social — Vertical News Video', footage: true },
        { genre_key: 'vertical_reframe', display_name: 'Vertical Reframe — Verbatim 9:16 Reformat', footage: true },
      ];
  const aspectOptions: string[] = caps?.aspects?.length ? caps.aspects : ['9:16', '16:9'];

  useEffect(() => {
    if (loaded && !initialized.current) {
      initialized.current = true;
      setStep(topicAtoms.length > 0 ? 'atoms' : topicSources.length > 0 ? 'atomize' : 'sources');
    }
  }, [loaded, topicAtoms.length, topicSources.length]);

  if (!loaded) {
    return (
      <div className="container mx-auto max-w-3xl px-6 py-16 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading topic…</p>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="container mx-auto max-w-3xl px-6 py-8">
        <p className="text-sm text-muted-foreground">{error ?? 'Topic not found.'}</p>
        <Button asChild variant="ghost" size="sm" className="mt-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>
    );
  }

  const steps: Step[] = [
    { key: 'sources', label: 'Source material', hint: 'What we loaded', icon: FileStack, count: topicSources.length },
    { key: 'atomize', label: 'Atomize', hint: 'Break it into atoms', icon: Sparkles },
    { key: 'atoms', label: 'Review atoms', hint: 'Approve or reject', icon: ListChecks, count: topicAtoms.length },
    { key: 'events', label: 'Events', hint: 'Group atoms into events', icon: Network, count: topicEvents.length },
  ];

  return (
    <div className="container mx-auto max-w-7xl px-6 py-8">
      <Button asChild variant="ghost" size="sm" className="mb-3">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" /> All topics
        </Link>
      </Button>

      {editingTitle ? (
        <input
          className="w-full max-w-2xl rounded-md border border-input bg-background px-2 py-1 text-2xl font-bold"
          value={titleDraft}
          autoFocus
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={() => {
            setEditingTitle(false);
            const t = titleDraft.trim();
            if (t && t !== topic.title) void renameTopic(id, t);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setEditingTitle(false);
          }}
        />
      ) : (
        <h1 className="group flex items-center gap-2 text-2xl font-bold">
          {topic.title}
          <button
            className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
            onClick={() => {
              setTitleDraft(topic.title);
              setEditingTitle(true);
            }}
            aria-label="Rename topic"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </h1>
      )}
      <p className="mt-1 text-sm text-muted-foreground">
        {topicSources.length} source{topicSources.length === 1 ? '' : 's'} · {topicAtoms.length} atom
        {topicAtoms.length === 1 ? '' : 's'}
      </p>

      {error && <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600">{error}</div>}

      <div className="my-6">
        <PipelineStepper steps={steps} active={step} onSelect={setStep} />
      </div>

      {step === 'sources' && (
        <SourcesStep
          sources={topicSources}
          canIngest={persistenceMode === 'cloud'}
          onAddFiles={(files, cb) => ingestIntoTopic(id, files, cb)}
          onDelete={(sourceId) => deleteSource(id, sourceId)}
          onContinue={() => setStep('atomize')}
        />
      )}
      {step === 'atomize' && (
        <AtomizeStep
          sources={topicSources}
          existingCount={topicAtoms.length}
          llmAvailable={persistenceMode === 'cloud'}
          onRun={async (engineKey) => {
            await atomizeTopic(id, engineKey);
            setStep('atoms');
          }}
        />
      )}
      {step === 'atoms' && (
        <AtomsStep
          atoms={topicAtoms}
          canPublish={persistenceMode === 'cloud'}
          onApprove={(aid) => void updateAtomStatus(aid, 'approved')}
          onReject={(aid) => void updateAtomStatus(aid, 'rejected')}
          onApproveAll={() => approveAll(id)}
          onGoAtomize={() => setStep('atomize')}
          onPublish={() => publishToIconik(id)}
          onProduce={(opts) => produce(id, opts)}
          genres={genreOptions}
          aspects={aspectOptions}
        />
      )}
      {step === 'events' && (
        <EventsStep
          topicId={id}
          events={topicEvents}
          atoms={topicAtoms}
          canCluster={persistenceMode === 'cloud' && topicAtoms.length > 0}
          onCluster={() => clusterEvents(id)}
          canProduce={persistenceMode === 'cloud'}
          onProduceEvent={(eventId, opts) => produceEvent(id, eventId, opts)}
          onSaveEvents={(draft) => saveEventsFor(id, draft)}
          onEnrichEvent={(input) => enrichEventDraft(id, input)}
          genres={genreOptions}
          aspects={aspectOptions}
        />
      )}
    </div>
  );
}

// --- Step 1: Sources -------------------------------------------------------

function SourcesStep({
  sources,
  canIngest,
  onAddFiles,
  onDelete,
  onContinue,
}: {
  sources: Source[];
  canIngest: boolean;
  onAddFiles: (files: File[], onProgress?: (done: number, total: number) => void) => Promise<unknown>;
  onDelete: (sourceId: string) => Promise<unknown>;
  onContinue: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  const [viewing, setViewing] = useState<Source | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  async function remove(sourceId: string) {
    setDeleting(sourceId);
    try {
      await onDelete(sourceId);
      if (open === sourceId) setOpen(null);
    } finally {
      setDeleting(null);
      setConfirming(null);
    }
  }

  async function addFiles(list: FileList | null) {
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;
    setAdding({ done: 0, total: files.length });
    await onAddFiles(files, (done, total) => setAdding({ done, total }));
    setAdding(null);
  }

  const addButton = canIngest ? (
    <label className="inline-flex cursor-pointer items-center rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent">
      {adding ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
      {adding ? `Analyzing ${adding.done}/${adding.total}…` : 'Add sources'}
      <input type="file" multiple className="hidden" disabled={!!adding} onChange={(e) => void addFiles(e.target.files)} />
    </label>
  ) : null;

  if (sources.length === 0) {
    return (
      <div className="rounded-lg border bg-card py-12 text-center">
        <FileStack className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">No source material yet.</p>
        <div className="mt-3 flex justify-center">{addButton}</div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">These are the raw items we loaded. Click one to see its content.</p>
        <span className="flex items-center gap-2">
          {addButton}
          <Button size="sm" onClick={onContinue}>
            Continue to Atomize <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </span>
      </div>
      <div className="space-y-2">
        {sources.map((s) => {
          const expanded = open === s.id;
          return (
            <div key={s.id} className="rounded-lg border bg-card">
              <div className="flex items-center gap-1 pr-3">
                <button
                  className="flex min-w-0 flex-1 items-center gap-3 p-3 text-left"
                  onClick={() => setOpen(expanded ? null : s.id)}
                >
                  <Film className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{s.title}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {s.externalId ? `${s.externalId} · ` : ''}
                      {s.capturedAt ?? 'no date'}
                      {s.duration ? ` · ${s.duration}` : ''}
                    </span>
                  </span>
                  {s.category && <Badge variant="secondary">{s.category}</Badge>}
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                <Button size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs" onClick={() => setViewing(s)}>
                  <Eye className="mr-1 h-3.5 w-3.5" />
                  View
                </Button>
                {confirming === s.id ? (
                  <span className="flex shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 px-2 text-xs"
                      disabled={deleting === s.id}
                      onClick={() => void remove(s.id)}
                    >
                      {deleting === s.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={deleting === s.id}
                      onClick={() => setConfirming(null)}
                    >
                      Cancel
                    </Button>
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => setConfirming(s.id)}
                    aria-label={`Delete ${s.title}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {expanded && (
                <div className="space-y-2 border-t border-border p-3">
                  <div>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Cleaned text (what the atomizer reads)
                    </p>
                    <p className="text-sm">
                      {s.normalizedText || <em className="text-muted-foreground">Empty / no usable content</em>}
                    </p>
                  </div>
                  <div>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Original</p>
                    <p className="whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">{s.rawText || '—'}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <MediaViewer source={viewing} open={viewing !== null} onOpenChange={(o) => !o && setViewing(null)} />
    </div>
  );
}

// --- Step 2: Atomize -------------------------------------------------------

function AtomizeStep({
  sources,
  existingCount,
  llmAvailable,
  onRun,
}: {
  sources: Source[];
  existingCount: number;
  llmAvailable: boolean;
  onRun: (engineKey: string) => void | Promise<void>;
}) {
  const [engineKey, setEngineKey] = useState('heuristic');
  const [running, setRunning] = useState(false);
  const enabledFor = (a: (typeof ATOMIZERS)[number]) => a.available || (a.key === 'llm' && llmAvailable);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-lg border bg-card p-5 shadow-sm">
        <h2 className="text-lg font-semibold">Atomize this topic</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The atomizer reads all {sources.length} source{sources.length === 1 ? '' : 's'} and pulls out the smallest meaningful
          pieces — events, facts, quotes, people, and footage. Nothing is invented; every atom is taken from the source and keeps
          a link back to it.
        </p>

        <p className="mb-2 mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Extraction engine</p>
        <div className="space-y-2">
          {ATOMIZERS.map((a) => {
            const enabled = enabledFor(a);
            return (
              <button
                key={a.key}
                disabled={!enabled}
                onClick={() => setEngineKey(a.key)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md border p-3 text-left',
                  a.key === engineKey && enabled ? 'border-violet-500/50 bg-violet-50 dark:bg-violet-950/40' : 'bg-background',
                  !enabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <span className="mt-0.5">
                  <span
                    className={cn(
                      'block h-3 w-3 rounded-full border',
                      a.key === engineKey && enabled ? 'border-violet-600 bg-violet-600' : 'border-muted-foreground/40',
                    )}
                  />
                </span>
                <span>
                  <span className="block text-sm font-medium">{a.label}</span>
                  <span className="block text-xs text-muted-foreground">{a.description}</span>
                  {a.key === 'llm' && !llmAvailable && (
                    <span className="mt-1 block text-[11px] text-amber-600">
                      Available once the backend is deployed (cloud mode).
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {existingCount > 0 && (
          <p className="mt-3 text-xs text-amber-600">
            Re-running replaces the {existingCount} existing atom{existingCount === 1 ? '' : 's'} for this topic.
          </p>
        )}

        <Button
          className="mt-4 w-full"
          disabled={sources.length === 0 || running}
          onClick={async () => {
            setRunning(true);
            try {
              await onRun(engineKey);
            } finally {
              setRunning(false);
            }
          }}
        >
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          Atomize {sources.length} source{sources.length === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}

// --- Step 3: Atoms ---------------------------------------------------------

const TYPE_ORDER: AtomType[] = ['event', 'fact', 'quote', 'entity', 'media', 'statistic', 'context'];

// Genre + aspect selectors shared by the produce actions (topic + event).
function GenreAspectControls({
  genres,
  aspects,
  genre,
  aspect,
  onGenre,
  onAspect,
  grounded,
  onGrounded,
  disabled,
}: {
  genres: GenreOption[];
  aspects: string[];
  genre: string;
  aspect: string;
  onGenre: (g: string) => void;
  onAspect: (a: string) => void;
  grounded: boolean;
  onGrounded: (g: boolean) => void;
  disabled?: boolean;
}) {
  const selectClass = 'h-9 rounded-md border border-input bg-background px-2 text-xs';
  return (
    <>
      <select className={selectClass} value={genre} onChange={(e) => onGenre(e.target.value)} disabled={disabled} title="Genre">
        {genres.map((g) => (
          <option key={g.genre_key} value={g.genre_key}>
            {g.display_name}
          </option>
        ))}
      </select>
      <select
        className={selectClass}
        value={aspect}
        onChange={(e) => onAspect(e.target.value)}
        disabled={disabled}
        title="Aspect ratio"
      >
        {aspects.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      <label
        className="flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-input bg-background px-2 text-xs"
        title="Ground the cut with the Context Repository (playbooks, specs, brand/style rules). Off = ungrounded A/B baseline."
      >
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={grounded}
          onChange={(e) => onGrounded(e.target.checked)}
          disabled={disabled}
        />
        Grounded
      </label>
    </>
  );
}

function AtomsStep({
  atoms,
  canPublish,
  onApprove,
  onReject,
  onApproveAll,
  onGoAtomize,
  onPublish,
  onProduce,
  genres,
  aspects,
}: {
  atoms: NewsAtom[];
  canPublish: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onApproveAll: () => void | Promise<unknown>;
  onGoAtomize: () => void;
  onPublish: () => Promise<
    | {
        count: number;
        assetId: string;
        sourcesMirrored?: number;
        events?: number;
        footageCollectionId?: string;
        footageMirrored?: number;
      }
    | undefined
  >;
  onProduce: (opts: ProduceOptions) => Promise<{ production_id?: string; project_id?: string; url?: string } | undefined>;
  genres: GenreOption[];
  aspects: string[];
}) {
  const [typeFilter, setTypeFilter] = useState<AtomType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'candidate' | 'approved'>('all');
  const [view, setView] = useState<'list' | 'cloud'>('list');
  const [genre, setGenre] = useState('');
  const [aspect, setAspect] = useState('');
  const [grounded, setGrounded] = useState(true);
  // Clamp to a currently-valid option — the picked value can go out-of-range after
  // the async capabilities load resolves with a narrower list.
  const effGenre = genres.some((g) => g.genre_key === genre) ? genre : genres[0]?.genre_key || 'cnn_social';
  // Default to vertical (9:16) for the CNN-vertical use case, not whatever the
  // engine lists first — the user can still pick 16:9.
  const effAspect = aspects.includes(aspect) ? aspect : aspects.includes('9:16') ? '9:16' : aspects[0] || '9:16';
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<{
    count: number;
    assetId: string;
    sourcesMirrored?: number;
    events?: number;
    footageCollectionId?: string;
    footageMirrored?: number;
  } | null>(null);
  const [producing, setProducing] = useState(false);
  const [produced, setProduced] = useState<{ production_id?: string; project_id?: string; url?: string } | null>(null);

  if (atoms.length === 0) {
    return (
      <div className="rounded-lg border bg-card py-12 text-center">
        <ListChecks className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">No atoms yet — run the atomizer first.</p>
        <Button size="sm" className="mt-3" onClick={onGoAtomize}>
          <Sparkles className="mr-1 h-4 w-4" /> Go to Atomize
        </Button>
      </div>
    );
  }

  const typesPresent = TYPE_ORDER.filter((t) => atoms.some((a) => a.atom_type === t));
  const approved = atoms.filter((a) => a.review_process.status === 'approved').length;
  const candidates = atoms.filter((a) => a.review_process.status === 'candidate').length;
  const corroborated = atoms.filter((a) => a.media_anchor.length > 1).length;

  const filtered = atoms.filter(
    (a) =>
      (typeFilter === 'all' || a.atom_type === typeFilter) &&
      (statusFilter === 'all' || a.review_process.status === statusFilter),
  );

  return (
    <div>
      {/* Summary */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{atoms.length} atoms</Badge>
        <Badge variant="success">{approved} approved</Badge>
        <Badge variant="info">{corroborated} corroborated</Badge>
        <span className="ml-auto flex items-center gap-2">
          {candidates > 0 && (
            <Button size="sm" variant="outline" onClick={() => void onApproveAll()}>
              <Check className="mr-1 h-4 w-4" /> Approve all ({candidates})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onGoAtomize}>
            <Sparkles className="mr-1 h-4 w-4" /> Re-atomize
          </Button>
          {canPublish && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={publishing}
                onClick={async () => {
                  setPublishing(true);
                  try {
                    const r = await onPublish();
                    if (r) setPublished(r);
                  } finally {
                    setPublishing(false);
                  }
                }}
              >
                {publishing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1 h-4 w-4" />}
                Publish to Iconik
              </Button>
              <GenreAspectControls
                genres={genres}
                aspects={aspects}
                genre={effGenre}
                aspect={effAspect}
                onGenre={setGenre}
                onAspect={setAspect}
                grounded={grounded}
                onGrounded={setGrounded}
                disabled={producing}
              />
              <Button
                size="sm"
                disabled={producing}
                onClick={async () => {
                  setProducing(true);
                  try {
                    const r = await onProduce({ genre: effGenre, aspect: effAspect, grounded });
                    if (r) setProduced(r);
                  } finally {
                    setProducing(false);
                  }
                }}
              >
                {producing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Clapperboard className="mr-1 h-4 w-4" />}
                Send to Story Engine
              </Button>
            </>
          )}
        </span>
      </div>

      {published && (
        <div className="mb-4 space-y-1">
          <p className="text-xs text-emerald-600">
            Published to Iconik: {published.count} atoms
            {published.events ? `, ${published.events} events` : ''}
            {published.sourcesMirrored ? `, ${published.sourcesMirrored} source file(s)` : ''}
            {published.footageMirrored ? ` (${published.footageMirrored} video)` : ''} — as timecoded segments the Story Engine
            reads.
          </p>
          {published.footageCollectionId && (
            <p className="font-mono text-[11px] text-muted-foreground">
              Footage collection: <span className="text-foreground">{published.footageCollectionId}</span>
            </p>
          )}
        </div>
      )}
      {produced && (
        <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
          <p className="text-xs font-medium text-emerald-700">
            ✅ Sent to Story Engine — production started{produced.production_id ? ` (${produced.production_id})` : ''}.
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            The producer approves the paper cut at the review gate in the Story Engine.
          </p>
          {produced.url && (
            <a
              href={produced.url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-[11px] font-medium text-violet-600 hover:underline"
            >
              Open in Story Engine ↗
            </a>
          )}
        </div>
      )}
      {!canPublish && (
        <p className="mb-4 text-[11px] text-muted-foreground">
          Publishing &amp; “Send to Story Engine” are available once the backend is deployed (cloud mode).
        </p>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        <FilterPill active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>
          All types
        </FilterPill>
        {typesPresent.map((t) => (
          <FilterPill key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)}>
            {t}
          </FilterPill>
        ))}
        <span className="mx-1 w-px bg-border" />
        <FilterPill
          active={statusFilter === 'candidate'}
          onClick={() => setStatusFilter(statusFilter === 'candidate' ? 'all' : 'candidate')}
        >
          Needs review
        </FilterPill>
        <FilterPill
          active={statusFilter === 'approved'}
          onClick={() => setStatusFilter(statusFilter === 'approved' ? 'all' : 'approved')}
        >
          Approved
        </FilterPill>
        {/* List / Cloud view toggle */}
        <span className="ml-auto inline-flex rounded-lg border bg-muted p-0.5">
          <button
            onClick={() => setView('list')}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition-colors',
              view === 'list' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <ListChecks className="h-3.5 w-3.5" /> List
          </button>
          <button
            onClick={() => setView('cloud')}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition-colors',
              view === 'cloud' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Network className="h-3.5 w-3.5" /> Cloud
          </button>
        </span>
      </div>

      {view === 'cloud' ? (
        <AtomCloud atoms={filtered} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <AtomCard key={a.atom_id} atom={a} onApprove={onApprove} onReject={onReject} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs capitalize transition-colors',
        active ? 'border-violet-500/50 bg-violet-600 text-white' : 'bg-card hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}

// --- Step 4: Events --------------------------------------------------------

function EventsStep({
  topicId,
  events,
  atoms,
  canCluster,
  onCluster,
  canProduce,
  onProduceEvent,
  onSaveEvents,
  onEnrichEvent,
  genres,
  aspects,
}: {
  topicId: string;
  events: StoryEvent[];
  atoms: NewsAtom[];
  canCluster: boolean;
  onCluster: () => void | Promise<unknown>;
  canProduce: boolean;
  onProduceEvent: (
    eventId: string,
    opts: ProduceOptions,
  ) => Promise<{ production_id?: string; project_id?: string; url?: string } | undefined>;
  onSaveEvents: (draft: StoryEvent[]) => Promise<StoryEvent[] | undefined>;
  onEnrichEvent: (input: EnrichEventInput) => Promise<EventEnrichment | undefined>;
  genres: GenreOption[];
  aspects: string[];
}) {
  const [view, setView] = useState<'aggregator' | 'grid'>('aggregator');
  const [busy, setBusy] = useState(false);
  const [genre, setGenre] = useState('');
  const [aspect, setAspect] = useState('');
  const [grounded, setGrounded] = useState(true);
  // Clamp to a currently-valid option — the picked value can go out-of-range after
  // the async capabilities load resolves with a narrower list.
  const effGenre = genres.some((g) => g.genre_key === genre) ? genre : genres[0]?.genre_key || 'cnn_social';
  // Default to vertical (9:16) for the CNN-vertical use case, not whatever the
  // engine lists first — the user can still pick 16:9.
  const effAspect = aspects.includes(aspect) ? aspect : aspects.includes('9:16') ? '9:16' : aspects[0] || '9:16';
  const [producingId, setProducingId] = useState<string | null>(null);
  const [producedById, setProducedById] = useState<Record<string, { production_id?: string; url?: string }>>({});
  const byId = new Map(atoms.map((a) => [a.atom_id, a]));
  const labelById = new Map(events.map((e) => [e.event_id, e.label]));

  async function run() {
    setBusy(true);
    try {
      await onCluster();
    } finally {
      setBusy(false);
    }
  }

  async function produce(eventId: string) {
    setProducingId(eventId);
    try {
      const r = await onProduceEvent(eventId, { genre: effGenre, aspect: effAspect, grounded });
      if (r) setProducedById((p) => ({ ...p, [eventId]: { production_id: r.production_id, url: r.url } }));
    } finally {
      setProducingId(null);
    }
  }

  const clusterBtn = canCluster ? (
    <Button size="sm" variant="outline" onClick={run} disabled={busy}>
      {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
      {busy ? 'Suggesting…' : events.length ? 'Re-suggest with AI' : 'Suggest with AI'}
    </Button>
  ) : null;

  const toggle = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-md border bg-muted/50 p-0.5">
        <button
          onClick={() => setView('aggregator')}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium transition',
            view === 'aggregator' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Aggregator
        </button>
        <button
          onClick={() => setView('grid')}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium transition',
            view === 'grid' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Overview
        </button>
      </div>
      <Badge variant="secondary">{events.length} events</Badge>
      <span className="ml-auto flex items-center gap-2">
        {view === 'grid' && canProduce && (
          <>
            <span className="text-[11px] text-muted-foreground">Produce as</span>
            <GenreAspectControls
              genres={genres}
              aspects={aspects}
              genre={effGenre}
              aspect={effAspect}
              onGenre={setGenre}
              onAspect={setAspect}
              grounded={grounded}
              onGrounded={setGrounded}
              disabled={producingId !== null}
            />
          </>
        )}
        {clusterBtn}
      </span>
    </div>
  );

  if (view === 'aggregator') {
    return (
      <div>
        {toggle}
        <EventAggregator
          topicId={topicId}
          atoms={atoms}
          events={events}
          canUseAI={canProduce}
          onSave={onSaveEvents}
          onEnrich={onEnrichEvent}
        />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div>
        {toggle}
        <div className="rounded-lg border bg-card py-12 text-center">
          <Network className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            No events yet. Build them by hand in the <span className="font-medium">Aggregator</span>, or let AI suggest a starting
            set — then refine.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {toggle}
      <div className="grid gap-3 lg:grid-cols-2">
        {events.map((e) => (
          <div key={e.event_id} className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-1 flex items-center gap-2">
              <Badge variant="info">
                <Network className="mr-1 h-3 w-3" />
                Event
              </Badge>
              <span className="text-sm font-medium">{e.label}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{e.atom_ids.length} atoms</span>
            </div>
            {(e.actor || e.action || e.object || e.location) && (
              <p className="mb-2 text-xs text-muted-foreground">
                {[e.actor, e.action, e.object, e.location].filter(Boolean).join(' · ')}
              </p>
            )}
            <ul className="space-y-1">
              {e.atom_ids.slice(0, 8).map((aid) => {
                const a = byId.get(aid);
                if (!a) return null;
                return (
                  <li key={aid} className="text-[12px] text-foreground">
                    <span className="text-muted-foreground">•</span> {a.statement}
                  </li>
                );
              })}
              {e.atom_ids.length > 8 && <li className="text-[11px] text-muted-foreground">+{e.atom_ids.length - 8} more</li>}
            </ul>
            {(e.related_event_ids?.length ?? 0) > 0 && (
              <div className="mt-3 border-t border-border pt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Related events</p>
                <div className="flex flex-wrap gap-1.5">
                  {e.related_event_ids!.map((rid) => (
                    <span
                      key={rid}
                      className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      <Network className="mr-1 h-3 w-3" />
                      {labelById.get(rid) ?? rid}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {canProduce && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <Button size="sm" disabled={producingId === e.event_id} onClick={() => void produce(e.event_id)}>
                  {producingId === e.event_id ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Clapperboard className="mr-1 h-4 w-4" />
                  )}
                  {producingId === e.event_id ? 'Sending…' : 'Produce CNN vertical'}
                </Button>
                {producedById[e.event_id] && (
                  <span className="text-[11px] text-emerald-600">
                    ✅ started
                    {producedById[e.event_id].production_id ? ` (${producedById[e.event_id].production_id})` : ''}
                    {producedById[e.event_id].url && (
                      <>
                        {' · '}
                        <a
                          href={producedById[e.event_id].url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-violet-600 hover:underline"
                        >
                          Open in Story Engine ↗
                        </a>
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
